import React from 'react';
import { observer } from 'mobx-react';
import { Theme, ThemeContext, ThemedStyles, useStyles } from 'ui/base/theme';
import baseStyles from './range_input.module.css';
import {
  RangeInput as BaseRangeInput,
  RangeInputProps as BaseRangeInputProps,
} from 'ui/base/input';

type LinearGradientColors = {
  lower: string;
  upper: string;
};

const linearGradientColors: Record<Theme, LinearGradientColors> = {
  [Theme.DAYLIGHT]: {
    lower: baseStyles.daylightLowerRangeColor,
    upper: baseStyles.daylightUpperRangeColor,
  },
  [Theme.MIDNIGHT]: {
    lower: baseStyles.midnightLowerRangeColor,
    upper: baseStyles.midnightUpperRangeColor,
  },
};

const getLinearGradientStyle = (props: RangeInputProps, theme: Theme): string => {
  const {
    min,
    max,
    base,
    ceil,
    invert,
    store,
  } = props;

  const realBase = (base !== undefined && base <= min) ? base : min;
  const realCeil = (ceil !== undefined && ceil >= max) ? ceil : max;
  const realValue = Math.min(Math.max(store.value, min), max);

  const colors = linearGradientColors[theme];

  const realLower = invert ? colors.upper : colors.lower;
  const realUpper = invert ? colors.lower : colors.upper;

  const c01 = `${realUpper}`;
  const c02 = `${realUpper} ${(min - realBase) / (realCeil - realBase) * 100}%`;
  const c03 = `${realLower} ${(min - realBase) / (realCeil - realBase) * 100}%`;

  const c04 = `${realLower}`;
  const c05 = `${realLower} ${(realValue - realBase) / (realCeil - realBase) * 100}%`;
  const c06 = `${realUpper} ${(realValue - realBase) / (realCeil - realBase) * 100}%`;
  const c07 = `${realUpper}`;

  const c08 = `${realUpper} ${(max - realBase) / (realCeil - realBase) * 100}%`;
  const c09 = `${realLower} ${(max - realBase) / (realCeil - realBase) * 100}%`;
  const c10 = `${realLower}`;

  return `linear-gradient(to right, ${c01}, ${c02}, ${c03}, ${c04}, ${c05}, ${c06}, ${c07}, ${c08}, ${c09}, ${c10})`;
};

type RangeInputStyles = {
  wrapper: string;
  range: string;
};

const themedStyles: ThemedStyles<RangeInputStyles> = {
  [Theme.DAYLIGHT]: {
    wrapper: baseStyles.wrapper,
    range: baseStyles.daylightRange,
  },
  [Theme.MIDNIGHT]: {
    wrapper: baseStyles.wrapper,
    range: baseStyles.midnightRange,
  },
};

export type RangeInputProps = Omit<BaseRangeInputProps, 'className'>;
export const RangeInput = observer((props: RangeInputProps) => {
  const styles = useStyles(themedStyles);
  const theme = React.useContext(ThemeContext);
  return (
    <div className={styles.wrapper}>
      <BaseRangeInput
        className={styles.range}
        style={{
          background: getLinearGradientStyle(props, theme),
        }}
        {...props}
      />
    </div>
  );
});
